import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Divider,
  Skeleton,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import BlockIcon from '@mui/icons-material/Block';
import FlagIcon from '@mui/icons-material/Flag';
import BugReportIcon from '@mui/icons-material/BugReport';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import PeopleIcon from '@mui/icons-material/People';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import SettingsIcon from '@mui/icons-material/Settings';
import StorageIcon from '@mui/icons-material/Storage';

interface QuickActionsProps {
  loading?: boolean;
}

interface QuickAction {
  label: string;
  description: string;
  icon: React.ReactNode;
  color: 'primary' | 'error' | 'warning' | 'success' | 'info';
  path: string;
}

/**
 * QuickActions - Sidebar with frequently used admin actions
 *
 * Provides quick access to:
 * - User management
 * - Content moderation
 * - System monitoring
 * - Analytics
 *
 * Features:
 * - Icon-based navigation
 * - Color-coded buttons
 * - Descriptions for each action
 * - Loading state support
 */
export default function QuickActions({ loading }: QuickActionsProps) {
  const navigate = useNavigate();

  const quickActions: QuickAction[] = [
    {
      label: 'Ban User',
      description: 'Suspend user accounts',
      icon: <BlockIcon />,
      color: 'error',
      path: '/admin/users',
    },
    {
      label: 'Review Flagged Content',
      description: 'Check reported homes and chats',
      icon: <FlagIcon />,
      color: 'warning',
      path: '/admin/content',
    },
    {
      label: 'View Error Logs',
      description: 'System errors and warnings',
      icon: <BugReportIcon />,
      color: 'error',
      path: '/admin/system',
    },
    {
      label: 'System Health',
      description: 'Monitor service status',
      icon: <HealthAndSafetyIcon />,
      color: 'success',
      path: '/admin/system',
    },
    {
      label: 'User Management',
      description: 'Search and manage users',
      icon: <PeopleIcon />,
      color: 'primary',
      path: '/admin/users',
    },
    {
      label: 'Analytics',
      description: 'View detailed metrics',
      icon: <AnalyticsIcon />,
      color: 'info',
      path: '/admin/analytics',
    },
    {
      label: 'Database Tools',
      description: 'SQL runner and queries',
      icon: <StorageIcon />,
      color: 'primary',
      path: '/admin/tools',
    },
    {
      label: 'Admin Settings',
      description: 'Feature flags and config',
      icon: <SettingsIcon />,
      color: 'primary',
      path: '/admin/tools',
    },
  ];

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Skeleton variant="text" width="60%" height={32} sx={{ mb: 2 }} />
          {[1, 2, 3, 4].map((i) => (
            <Box key={i} sx={{ mb: 2 }}>
              <Skeleton variant="rectangular" height={56} />
            </Box>
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Quick Actions
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Common administrative tasks
        </Typography>

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {quickActions.map((action, index) => (
            <Button
              key={index}
              variant="outlined"
              color={action.color}
              startIcon={action.icon}
              onClick={() => navigate(action.path)}
              sx={{
                justifyContent: 'flex-start',
                textAlign: 'left',
                py: 3,
                px: 2,
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: `${action.color}.50`,
                  transform: 'translateX(4px)',
                  transition: 'all 0.2s',
                },
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {action.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {action.description}
                </Typography>
              </Box>
            </Button>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}
